import { inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MessageContent } from '@reflact/ai-types';
import { AuditLog, FallAufgabenStatus } from '@reflact/ipkv-types';
import { AuditlogService } from '../../../services/auditlog';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class SessionDetailsService {

  public isDetailsVisible = false

  private sanitizer = inject(DomSanitizer);
  private auditService = inject(AuditlogService);
  private httpClient = inject(HttpClient);
  public auditLogs: AuditLog<string, Date>[] = [];

  public connectedDuration: string = "";

  constructor() { }

  public async openSessionDetails(status: FallAufgabenStatus) {
    await this.auditService.list({ statusId: status._id });
    this.auditLogs = this.auditService.auditLogs.sort((a, b) => a.created.getTime() - b.created.getTime());

    let completeDuration: number = 0



    for (let i = 0; i < this.auditLogs.length; i++) {
      const log = this.auditLogs[i];

      if (log.msg.startsWith("Nutzerverbindung wurde aufgebaut")) {
        const routeInfo = log.data.routeInfo
        let endLog = this.auditLogs.find(log => log.msg.startsWith("Nutzerverbindung wurde getrennt") && log.data.routeInfo == routeInfo)
        const endTime = endLog == null ? Date.now() : endLog.created.getTime()
        completeDuration += endTime - log.created.getTime()
      }
    }
    this.connectedDuration = this.formatMilliseconds(completeDuration);
    this.isDetailsVisible = true;
  }

  private formatMilliseconds(ms: number): string {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms / 60000) % 60);
    return hours.toString().padStart(2, '0') + 'h ' + minutes.toString().padStart(2, '0') + "m";
  }

  public getContentFromMsgItem(msg: MessageContent[]) {
    const m = msg.at(0);
    if (m && m.type === 'text') return m.text;
    return '';
  }

  public isHtmlCard(msg: any): boolean {
    try {
      const content = msg?.content ?? msg;
      if (!content) return false;
      const tryParse = (v: any) => {
        if (typeof v === 'string') {
          try { return JSON.parse(v); } catch { return null; }
        }
        return v ?? null;
      };
      const parsed = tryParse(content);
      if (parsed?.type === 'htmlCard') return true;
      if (Array.isArray(content)) {
        for (const el of content) {
          if (!el) continue;
          if (el.type === 'htmlCard') return true;
          const candidate = tryParse(el.json ?? el.text ?? el);
          if (candidate?.type === 'htmlCard') return true;
        }
      }
      return false;
    } catch {
      return false;
    }
  }

  public getHtmlCard(msg: any): SafeHtml {
    try {
      const raw = msg?.content ?? msg;
      if (!raw) return this.sanitizer.bypassSecurityTrustHtml('');
      let parsed: any = null;
      if (typeof raw === 'string') {
        try { parsed = JSON.parse(raw); } catch { }
      } else if (Array.isArray(raw)) {
        for (const el of raw) {
          if (!el) continue;
          if (el.type === 'htmlCard') { parsed = el; break; }
          if (el.type === 'text' && typeof el.text === 'string') {
            try {
              const p = JSON.parse(el.text);
              if (p?.type === 'htmlCard') { parsed = p; break; }
            } catch { }
          }
          if (el.type === 'json' && el.json) {
            try {
              const p = typeof el.json === 'string' ? JSON.parse(el.json) : el.json;
              if (p?.type === 'htmlCard') { parsed = p; break; }
            } catch { }
          }
        }
      } else if (typeof raw === 'object') {
        parsed = raw;
      }
      if (!parsed) return this.sanitizer.bypassSecurityTrustHtml('');
      const card = parsed.type === 'htmlCard' ? (parsed.data ?? {}) : parsed;
      if (card.html && typeof card.html === 'string') {
        return this.sanitizer.bypassSecurityTrustHtml(card.html);
      }
      const title = card.title ? `<h3 style="margin:0 0 .5rem">${this.escapeHtml(card.title)}</h3>` : '';
      const body = card.body ? `<div>${card.body}</div>` : (card.content ? `<div>${this.escapeHtml(card.content)}</div>` : '');
      let buttons = '';
      if (Array.isArray(card.buttons)) {
        buttons = '<div style="margin-top:.5rem">';
        for (const b of card.buttons) {
          const label = this.escapeHtml(b?.label || 'Aktion');
          const href = this.escapeAttr(b?.url || '#');
          buttons += `<a href="${href}" target="_blank" style="display:inline-block;margin-right:.25rem;padding:.25rem .5rem;border-radius:.25rem;background:#eee;text-decoration:none">${label}</a>`;
        }
        buttons += '</div>';
      }
      const cssClass = card.cssClass ? ` class="${this.escapeHtml(card.cssClass)}"` : '';
      const cardHtml = `<div${cssClass} style="padding:.75rem;border-radius:.5rem;background:#fff">${title}${body}${buttons}</div>`;
      return this.sanitizer.bypassSecurityTrustHtml(cardHtml);
    } catch {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
  }

  private escapeHtml(str: string): string {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  public isButtonsCard(msg: any): boolean {
    try {
      const content = msg?.content ?? msg;
      if (!content) return false;
      const tryParse = (v: any) => {
        if (typeof v === 'string') {
          try { return JSON.parse(v); } catch { return null; }
        }
        return v ?? null;
      };
      const parsed = tryParse(content);
      if (parsed?.type === 'buttonsCard') return true;
      if (Array.isArray(content)) {
        for (const el of content) {
          if (!el) continue;
          if (el.type === 'buttonsCard') return true;
          const candidate = tryParse(el.json ?? el.text ?? el);
          if (candidate?.type === 'buttonsCard') return true;
        }
      }
      return false;
    } catch {
      return false;
    }
  }

  private escapeAttr(str: string): string {
    return this.escapeHtml(str).replace(/"/g, '%22');
  }

  public getMessageForLogEntry(log: AuditLog) {
    if (log.msg != "") {
      return log.msg
    }

    if (log.data.routeInfo == "200 [POST] /api/interact/chat") {
      return "Eine Nutzernachricht wurde dem Chatverlauf hinzugefügt"

    }
    if (log.data.routeInfo == "200 [POST] /api/interact/addMessage") {
      return "Eine Nachricht wurde dem Chatverlauf hinzugefügt"
    }

    if (log.data.routeInfo == "200 [POST] /api/interact/selectaufgabe") {
      return "Eine Aufgabe wurde ausgewählt"

    }


    return log.data.routeInfo

  }

  public async regenerateExplanation(customPrompt: string): Promise<string> {
    try {
      const response = await this.httpClient
        .post<{ explanation: string }>(
          '/api/admin/fallaufgabenstatus/regenerate-explanation',
          { customPrompt }
        )
        .toPromise();
      return response?.explanation ?? '';
    } catch (error) {
      console.error('Regeneration failed:', error);
      throw error;
    }
  }
}
